import {
    ChartPattern,
    CandlestickPatternKind,
    ChartPatternBias,
    ChartPatternDetectOptions,
} from './chartPatternTypes'
import { Time } from 'lightweight-charts'
import { limitPatterns, PATTERN_DETECTION_LOOKBACK_BARS, sliceRecentOpenHighLowClose, toPatternPoint } from './patternUtils'
import { getCandlestickPatternDescription } from './candlestickPatternDescriptions'
import {
    OhlcCandle,
    bodyBottom,
    bodyTop,
    candleBody,
    hasGapDown,
    hasGapUp,
    hasLongLowerShadow,
    hasLongUpperShadow,
    hasTinyLowerShadow,
    hasTinyUpperShadow,
    isBearish,
    isBullish,
    isDoji,
    isHammerShaped,
    isLongBody,
    isNearSwingHigh,
    isNearSwingLow,
    isShootingStarShaped,
    isSmallBody,
    isTradingViewHammer,
    isTradingViewInvertedHammer,
    isTradingViewLongBody,
    isTradingViewSmallBody,
    isTradingViewDojiBody,
    hasTradingViewUpShadow,
    hasTradingViewDownShadow,
    haveNotUpShadow,
    bodyMiddle,
    computeBodyEma,
    computeTrendSmaSeries,
    passesBullishTrendFilter,
    passesBearishTrendFilter,
    pricesNear,
    pricesWithinBodyAvgTolerance,
    toOhlc,
} from './candlestickUtils'
import { getTradingViewCandlestickMarkerLetter, getTradingViewCandlestickPatternName } from '../../../assets/candlestickPatternNames'

type OhlcSeries = {
    open: number[]
    high: number[]
    low: number[]
    close: number[]
    timestamps: number[]
}

type DetectorFn = (series: OhlcSeries, endIdx: number) => number[] | null

function scanAllPatterns(
    series: OhlcSeries,
    detect: DetectorFn,
): number[][] {
    const { close } = series
    const matches: number[][] = []
    const seen = new Set<string>()
    for (let i = close.length - 1; i >= 0; i--) {
        const match = detect(series, i)
        if (!match?.length) continue
        const key = match.join('-')
        if (seen.has(key)) continue
        seen.add(key)
        matches.push(match)
    }
    return matches
}

function buildCandlestickPattern(
    kind: CandlestickPatternKind,
    bias: ChartPatternBias,
    indices: number[],
    high: number[],
    low: number[],
    close: number[],
    timestamps: number[],
    label: string,
    markerLetter: string,
): ChartPattern {
    const anchorIdx = indices[indices.length - 1]
    const anchorClose = close[anchorIdx]
    return {
        kind,
        bias,
        displayMode: 'candlestick',
        markerLetter,
        candleHigh: high[anchorIdx],
        candleLow: low[anchorIdx],
        candleClose: anchorClose,
        highlightCandleTimes: indices.map((idx) => Math.floor(timestamps[idx] / 1000) as Time),
        description: getCandlestickPatternDescription(kind),
        points: [toPatternPoint('anchor', timestamps[anchorIdx], anchorClose)],
        labels: [{
            pointId: 'anchor',
            text: label,
            position: bias === 'bullish' ? 'below' : 'above',
        }],
    }
}

function detectAt(
    series: OhlcSeries,
    detect: DetectorFn,
    kind: CandlestickPatternKind,
    bias: ChartPatternBias,
    label: string,
    markerLetter: string,
    options: ChartPatternDetectOptions,
): ChartPattern[] {
    const allMatches = scanAllPatterns(series, detect)
    if (!allMatches.length) return []
    const patterns = allMatches.map((indices) => buildCandlestickPattern(
        kind,
        bias,
        indices,
        series.high,
        series.low,
        series.close,
        series.timestamps,
        label,
        markerLetter,
    ))
    return limitPatterns(patterns, options.maxPatterns ?? 0, options.inProgress ?? true)
}

const detectBullishEngulfingAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 1) return null
    const prev = toOhlc(open, high, low, close, i - 1)
    const curr = toOhlc(open, high, low, close, i)
    if (!prev || !curr) return null
    if (!isBearish(prev) || !isBullish(curr)) return null
    if (curr.open > prev.close || curr.close < prev.open) return null
    if (candleBody(curr) <= candleBody(prev)) return null
    return [i - 1, i]
}

const detectHammerAt: DetectorFn = ({ open, high, low, close }, i) => {
    const c = toOhlc(open, high, low, close, i)
    if (!c) return null
    const bodyEma = computeBodyEma(open, close)
    if (!isTradingViewHammer(c, bodyEma[i])) return null
    return [i]
}

function createHammerDetector(trendBasedOn?: string): DetectorFn {
    let cachedClose: number[] | null = null
    let bodyEma: number[] | null = null
    let sma50: number[] | null = null
    let sma200: number[] | null = null

    return ({ open, high, low, close }, i) => {
        if (cachedClose !== close) {
            cachedClose = close
            bodyEma = computeBodyEma(open, close)
            const trendSeries = computeTrendSmaSeries(close)
            sma50 = trendSeries.sma50
            sma200 = trendSeries.sma200
        }
        const c = toOhlc(open, high, low, close, i)
        if (!c || !bodyEma || !isTradingViewHammer(c, bodyEma[i])) return null
        if (!passesBullishTrendFilter(close, i, trendBasedOn, { sma50: sma50 ?? undefined, sma200: sma200 ?? undefined })) return null
        return [i]
    }
}

function createInvertedHammerDetector(trendBasedOn?: string): DetectorFn {
    let cachedClose: number[] | null = null
    let bodyEma: number[] | null = null
    let sma50: number[] | null = null
    let sma200: number[] | null = null

    return ({ open, high, low, close }, i) => {
        if (cachedClose !== close) {
            cachedClose = close
            bodyEma = computeBodyEma(open, close)
            const trendSeries = computeTrendSmaSeries(close)
            sma50 = trendSeries.sma50
            sma200 = trendSeries.sma200
        }
        const c = toOhlc(open, high, low, close, i)
        if (!c || !bodyEma || !isTradingViewInvertedHammer(c, bodyEma[i])) return null
        if (!passesBullishTrendFilter(close, i, trendBasedOn, { sma50: sma50 ?? undefined, sma200: sma200 ?? undefined })) return null
        return [i]
    }
}

function createHangingManDetector(trendBasedOn?: string): DetectorFn {
    let cachedClose: number[] | null = null
    let bodyEma: number[] | null = null
    let sma50: number[] | null = null
    let sma200: number[] | null = null

    return ({ open, high, low, close }, i) => {
        if (cachedClose !== close) {
            cachedClose = close
            bodyEma = computeBodyEma(open, close)
            const trendSeries = computeTrendSmaSeries(close)
            sma50 = trendSeries.sma50
            sma200 = trendSeries.sma200
        }
        const c = toOhlc(open, high, low, close, i)
        if (!c || !bodyEma || !isTradingViewHammer(c, bodyEma[i])) return null
        if (!passesBearishTrendFilter(close, i, trendBasedOn, { sma50: sma50 ?? undefined, sma200: sma200 ?? undefined })) return null
        return [i]
    }
}

function createShootingStarDetector(trendBasedOn?: string): DetectorFn {
    let cachedClose: number[] | null = null
    let bodyEma: number[] | null = null
    let sma50: number[] | null = null
    let sma200: number[] | null = null

    return ({ open, high, low, close }, i) => {
        if (cachedClose !== close) {
            cachedClose = close
            bodyEma = computeBodyEma(open, close)
            const trendSeries = computeTrendSmaSeries(close)
            sma50 = trendSeries.sma50
            sma200 = trendSeries.sma200
        }
        const c = toOhlc(open, high, low, close, i)
        if (!c || !bodyEma || !isTradingViewInvertedHammer(c, bodyEma[i])) return null
        if (!passesBearishTrendFilter(close, i, trendBasedOn, { sma50: sma50 ?? undefined, sma200: sma200 ?? undefined })) return null
        return [i]
    }
}

function wrapWithTrendFilter(
    detect: DetectorFn,
    bias: ChartPatternBias,
    trendBasedOn?: string,
): DetectorFn {
    let cachedClose: number[] | null = null
    let sma50: number[] | null = null
    let sma200: number[] | null = null

    return (series, i) => {
        const match = detect(series, i)
        if (!match?.length) return null

        const { close } = series
        if (cachedClose !== close) {
            cachedClose = close
            const trendSeries = computeTrendSmaSeries(close)
            sma50 = trendSeries.sma50
            sma200 = trendSeries.sma200
        }

        const anchorIdx = match[match.length - 1]
        const precomputed = { sma50: sma50 ?? undefined, sma200: sma200 ?? undefined }
        const passesTrend = bias === 'bearish'
            ? passesBearishTrendFilter(close, anchorIdx, trendBasedOn, precomputed)
            : passesBullishTrendFilter(close, anchorIdx, trendBasedOn, precomputed)
        return passesTrend ? match : null
    }
}

function resolvePatternDetector(def: CandlestickPatternDef, options: ChartPatternDetectOptions): DetectorFn {
    switch (def.kind) {
        case 'HAMMER':
            return createHammerDetector(options.trendBasedOn)
        case 'INVERTED_HAMMER':
            return createInvertedHammerDetector(options.trendBasedOn)
        case 'HANGING_MAN':
            return createHangingManDetector(options.trendBasedOn)
        case 'SHOOTING_STAR':
            return createShootingStarDetector(options.trendBasedOn)
        case 'MORNING_STAR':
            return createMorningStarDetector(options.trendBasedOn)
        case 'PIERCING_LINE':
            return createPiercingLineDetector(options.trendBasedOn)
        case 'THREE_WHITE_SOLDIERS':
            return createThreeWhiteSoldiersDetector()
        case 'TWEEZER_BOTTOM':
            return createTweezerBottomDetector(options.trendBasedOn)
        case 'TWEEZER_TOP':
            return createTweezerTopDetector(options.trendBasedOn)
        default:
            return wrapWithTrendFilter(def.detectAt, def.bias, options.trendBasedOn)
    }
}

function createTradingViewBodyDetector(
    detectAt: (series: OhlcSeries, i: number, bodyEma: number[]) => number[] | null,
): DetectorFn {
    let cachedClose: number[] | null = null
    let bodyEma: number[] | null = null

    return (series, i) => {
        const { open, close } = series
        if (cachedClose !== close) {
            cachedClose = close
            bodyEma = computeBodyEma(open, close)
        }
        if (!bodyEma) return null
        return detectAt(series, i, bodyEma)
    }
}

function createTradingViewBodyTrendDetector(
    detectAt: (
        series: OhlcSeries,
        i: number,
        bodyEma: number[],
        precomputed: { sma50?: number[]; sma200?: number[] },
    ) => number[] | null,
): DetectorFn {
    let cachedClose: number[] | null = null
    let bodyEma: number[] | null = null
    let sma50: number[] | null = null
    let sma200: number[] | null = null

    return (series, i) => {
        const { open, close } = series
        if (cachedClose !== close) {
            cachedClose = close
            bodyEma = computeBodyEma(open, close)
            const trendSeries = computeTrendSmaSeries(close)
            sma50 = trendSeries.sma50
            sma200 = trendSeries.sma200
        }
        if (!bodyEma) return null
        return detectAt(series, i, bodyEma, { sma50: sma50 ?? undefined, sma200: sma200 ?? undefined })
    }
}

function createMorningStarDetector(trendBasedOn?: string): DetectorFn {
    return createTradingViewBodyTrendDetector(({ open, high, low, close }, i, bodyEma, precomputed) => {
        if (i < 2) return null
        const c1 = toOhlc(open, high, low, close, i - 2)
        const c2 = toOhlc(open, high, low, close, i - 1)
        const c3 = toOhlc(open, high, low, close, i)
        if (!c1 || !c2 || !c3) return null
        if (!isTradingViewLongBody(c1, bodyEma[i - 2]) || !isTradingViewSmallBody(c2, bodyEma[i - 1]) || !isTradingViewLongBody(c3, bodyEma[i])) return null
        if (!passesBullishTrendFilter(close, i, trendBasedOn, precomputed)) return null
        if (!isBearish(c1) || !isBullish(c3)) return null
        if (bodyTop(c2) >= bodyBottom(c1)) return null
        if (bodyTop(c3) < bodyMiddle(c1) || bodyTop(c3) >= bodyTop(c1)) return null
        if (bodyTop(c2) >= bodyBottom(c3)) return null
        return [i - 2, i - 1, i]
    })
}

function createPiercingLineDetector(trendBasedOn?: string): DetectorFn {
    return createTradingViewBodyTrendDetector(({ open, high, low, close }, i, bodyEma, precomputed) => {
        if (i < 1) return null
        const prev = toOhlc(open, high, low, close, i - 1)
        const curr = toOhlc(open, high, low, close, i)
        if (!prev || !curr) return null
        if (!passesBullishTrendFilter(close, i - 1, trendBasedOn, precomputed)) return null
        if (!isBearish(prev) || !isBullish(curr)) return null
        if (!isTradingViewLongBody(prev, bodyEma[i - 1])) return null
        if (curr.open > prev.low) return null
        if (curr.close <= bodyMiddle(prev) || curr.close >= prev.open) return null
        return [i - 1, i]
    })
}

function createThreeWhiteSoldiersDetector(): DetectorFn {
    return createTradingViewBodyDetector(({ open, high, low, close }, i, bodyEma) => {
        if (i < 2) return null
        const candles = [i - 2, i - 1, i].map((idx) => toOhlc(open, high, low, close, idx))
        if (candles.some((c) => !c)) return null
        const [c1, c2, c3] = candles as OhlcCandle[]
        if (!isBullish(c1) || !isBullish(c2) || !isBullish(c3)) return null
        if (!isTradingViewLongBody(c1, bodyEma[i - 2]) || !isTradingViewLongBody(c2, bodyEma[i - 1]) || !isTradingViewLongBody(c3, bodyEma[i])) return null
        if (c3.close <= c2.close || c2.close <= c1.close) return null
        if (c3.open >= c2.close || c3.open <= c2.open) return null
        if (c2.open >= c1.close || c2.open <= c1.open) return null
        if (!haveNotUpShadow(c1) || !haveNotUpShadow(c2) || !haveNotUpShadow(c3)) return null
        return [i - 2, i - 1, i]
    })
}

function createTweezerBottomDetector(trendBasedOn?: string): DetectorFn {
    return createTradingViewBodyTrendDetector(({ open, high, low, close }, i, bodyEma, precomputed) => {
        if (i < 1) return null
        const prev = toOhlc(open, high, low, close, i - 1)
        const curr = toOhlc(open, high, low, close, i)
        if (!prev || !curr) return null
        if (!passesBearishTrendFilter(close, i - 1, trendBasedOn, precomputed)) return null
        if (isTradingViewDojiBody(curr) && !(hasTradingViewUpShadow(curr) && hasTradingViewDownShadow(curr))) return null
        if (!pricesWithinBodyAvgTolerance(prev.low, curr.low, bodyEma[i])) return null
        if (!isBearish(prev) || !isBullish(curr)) return null
        if (!isTradingViewLongBody(prev, bodyEma[i - 1])) return null
        return [i - 1, i]
    })
}

function createTweezerTopDetector(trendBasedOn?: string): DetectorFn {
    return createTradingViewBodyTrendDetector(({ open, high, low, close }, i, bodyEma, precomputed) => {
        if (i < 1) return null
        const prev = toOhlc(open, high, low, close, i - 1)
        const curr = toOhlc(open, high, low, close, i)
        if (!prev || !curr) return null
        if (!passesBearishTrendFilter(close, i - 1, trendBasedOn, precomputed)) return null
        if (isTradingViewDojiBody(curr) && !(hasTradingViewUpShadow(curr) && hasTradingViewDownShadow(curr))) return null
        if (!pricesWithinBodyAvgTolerance(prev.high, curr.high, bodyEma[i])) return null
        if (!isBullish(prev) || !isBearish(curr)) return null
        if (!isTradingViewLongBody(prev, bodyEma[i - 1])) return null
        return [i - 1, i]
    })
}

const detectMorningStarAt: DetectorFn = createMorningStarDetector()
const detectPiercingLineAt: DetectorFn = createPiercingLineDetector()
const detectThreeWhiteSoldiersAt: DetectorFn = createThreeWhiteSoldiersDetector()
const detectTweezerBottomAt: DetectorFn = createTweezerBottomDetector()
const detectTweezerTopAt: DetectorFn = createTweezerTopDetector()

const detectBullishHaramiAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 1) return null
    const prev = toOhlc(open, high, low, close, i - 1)
    const curr = toOhlc(open, high, low, close, i)
    if (!prev || !curr) return null
    if (!isBearish(prev) || !isBullish(curr)) return null
    if (!isLongBody(prev) || !isSmallBody(curr)) return null
    if (bodyTop(curr) > bodyTop(prev) || bodyBottom(curr) < bodyBottom(prev)) return null
    return [i - 1, i]
}

const detectInvertedHammerAt: DetectorFn = ({ open, high, low, close }, i) => {
    const c = toOhlc(open, high, low, close, i)
    if (!c) return null
    const bodyEma = computeBodyEma(open, close)
    if (!isTradingViewInvertedHammer(c, bodyEma[i])) return null
    return [i]
}

const detectDragonflyDojiAt: DetectorFn = ({ open, high, low, close }, i) => {
    const c = toOhlc(open, high, low, close, i)
    if (!c || !isDoji(c) || !isNearSwingLow(low, i)) return null
    if (!hasLongLowerShadow(c) || !hasTinyUpperShadow(c)) return null
    return [i]
}

const detectBullishAbandonedBabyAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 2) return null
    const c1 = toOhlc(open, high, low, close, i - 2)
    const c2 = toOhlc(open, high, low, close, i - 1)
    const c3 = toOhlc(open, high, low, close, i)
    if (!c1 || !c2 || !c3) return null
    if (!isBearish(c1) || !isDoji(c2) || !isBullish(c3)) return null
    if (!isLongBody(c1) || !isLongBody(c3)) return null
    if (!hasGapDown(c1, c2) || !hasGapUp(c2, c3)) return null
    return [i - 2, i - 1, i]
}

const detectThreeInsideUpAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 2) return null
    const c1 = toOhlc(open, high, low, close, i - 2)
    const c2 = toOhlc(open, high, low, close, i - 1)
    const c3 = toOhlc(open, high, low, close, i)
    if (!c1 || !c2 || !c3) return null
    if (!isBearish(c1) || !isBullish(c2) || !isBullish(c3)) return null
    const midpoint = (c1.open + c1.close) / 2
    if (bodyTop(c2) > bodyTop(c1) || bodyBottom(c2) < bodyBottom(c1)) return null
    if (c2.close <= midpoint || c3.close <= c1.open) return null
    return [i - 2, i - 1, i]
}

const detectThreeOutsideUpAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 2) return null
    const c1 = toOhlc(open, high, low, close, i - 2)
    const c2 = toOhlc(open, high, low, close, i - 1)
    const c3 = toOhlc(open, high, low, close, i)
    if (!c1 || !c2 || !c3) return null
    if (!isBearish(c1) || !isBullish(c2) || !isBullish(c3)) return null
    if (c2.open > c1.close || c2.close < c1.open) return null
    if (candleBody(c2) <= candleBody(c1)) return null
    if (c3.close <= c2.close) return null
    return [i - 2, i - 1, i]
}

const detectBullishKickerAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 1) return null
    const prev = toOhlc(open, high, low, close, i - 1)
    const curr = toOhlc(open, high, low, close, i)
    if (!prev || !curr) return null
    if (!isBearish(prev) || !isBullish(curr)) return null
    if (!isLongBody(prev) || !isLongBody(curr)) return null
    if (curr.open <= prev.open) return null
    return [i - 1, i]
}

const detectRisingThreeMethodsAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 4) return null
    const indices = [i - 4, i - 3, i - 2, i - 1, i]
    const candles = indices.map((idx) => toOhlc(open, high, low, close, idx))
    if (candles.some((c) => !c)) return null
    const [c1, c2, c3, c4, c5] = candles as OhlcCandle[]
    if (!isBullish(c1) || !isLongBody(c1)) return null
    if (!isBearish(c2) || !isBearish(c3) || !isBearish(c4)) return null
    if (!isBullish(c5) || !isLongBody(c5)) return null
    const rangeHigh = c1.high
    const rangeLow = c1.low
    if ([c2, c3, c4].some((c) => c.low < rangeLow || c.high > rangeHigh)) return null
    if (c5.close <= c1.high) return null
    return indices
}

const detectConcealingBabySwallowAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 3) return null
    const c1 = toOhlc(open, high, low, close, i - 3)
    const c2 = toOhlc(open, high, low, close, i - 2)
    const c3 = toOhlc(open, high, low, close, i - 1)
    const c4 = toOhlc(open, high, low, close, i)
    if (!c1 || !c2 || !c3 || !c4) return null
    if (!isBearish(c1) || !isBearish(c2) || !isBearish(c4)) return null
    if (!isLongBody(c1) || !isLongBody(c2) || !isLongBody(c4)) return null
    if (!hasGapDown(c2, c3)) return null
    if (c4.high < c3.high || c4.low > c3.low) return null
    return [i - 3, i - 2, i - 1, i]
}

const detectMatHoldAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 4) return null
    const indices = [i - 4, i - 3, i - 2, i - 1, i]
    const candles = indices.map((idx) => toOhlc(open, high, low, close, idx))
    if (candles.some((c) => !c)) return null
    const [c1, c2, c3, c4, c5] = candles as OhlcCandle[]
    if (!isBullish(c1) || !isLongBody(c1)) return null
    if (!isBearish(c2) || !isBearish(c3) || !isBearish(c4)) return null
    if (!isBullish(c5) || !isLongBody(c5)) return null
    if ([c2, c3, c4].some((c) => c.low < c1.low || c.high > c1.high)) return null
    if (c5.close <= c1.high) return null
    return indices
}

const detectBullishSeparatingLinesAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 1) return null
    const prev = toOhlc(open, high, low, close, i - 1)
    const curr = toOhlc(open, high, low, close, i)
    if (!prev || !curr) return null
    if (!isBearish(prev) || !isBullish(curr)) return null
    if (!pricesNear(prev.open, curr.open)) return null
    return [i - 1, i]
}

const detectBullishBeltHoldAt: DetectorFn = ({ open, high, low, close }, i) => {
    const c = toOhlc(open, high, low, close, i)
    if (!c || !isNearSwingLow(low, i)) return null
    if (!isBullish(c) || !isLongBody(c)) return null
    if (!hasTinyLowerShadow(c)) return null
    if (c.open > c.low * 1.002) return null
    return [i]
}

const detectThreeLineStrikeBullishAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 3) return null
    const c1 = toOhlc(open, high, low, close, i - 3)
    const c2 = toOhlc(open, high, low, close, i - 2)
    const c3 = toOhlc(open, high, low, close, i - 1)
    const c4 = toOhlc(open, high, low, close, i)
    if (!c1 || !c2 || !c3 || !c4) return null
    if (!isBullish(c1) || !isBullish(c2) || !isBullish(c3) || !isBearish(c4)) return null
    if (!isLongBody(c1) || !isLongBody(c2) || !isLongBody(c3) || !isLongBody(c4)) return null
    if (c4.open > c3.close || c4.close > c1.open) return null
    return [i - 3, i - 2, i - 1, i]
}

const detectLadderBottomAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 4) return null
    const bearish = [i - 4, i - 3, i - 2].map((idx) => toOhlc(open, high, low, close, idx))
    const small = toOhlc(open, high, low, close, i - 1)
    const bullish = toOhlc(open, high, low, close, i)
    if (bearish.some((c) => !c) || !small || !bullish) return null
    if (!bearish.every((c) => isBearish(c!) && isLongBody(c!))) return null
    if (!isBullish(bullish) || !isLongBody(bullish)) return null
    return [i - 4, i - 3, i - 2, i - 1, i]
}

const detectMeetingLinesAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 1) return null
    const prev = toOhlc(open, high, low, close, i - 1)
    const curr = toOhlc(open, high, low, close, i)
    if (!prev || !curr) return null
    if (!isBearish(prev) || !isBullish(curr)) return null
    if (!isLongBody(prev) || !isLongBody(curr)) return null
    if (!pricesNear(prev.close, curr.close)) return null
    if (curr.open >= prev.close) return null
    return [i - 1, i]
}

const detectBearishEngulfingAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 1) return null
    const prev = toOhlc(open, high, low, close, i - 1)
    const curr = toOhlc(open, high, low, close, i)
    if (!prev || !curr) return null
    if (!isBullish(prev) || !isBearish(curr)) return null
    if (curr.open < prev.close || curr.close > prev.open) return null
    if (candleBody(curr) <= candleBody(prev)) return null
    return [i - 1, i]
}

const detectBearishBeltHoldAt: DetectorFn = ({ open, high, low, close }, i) => {
    const c = toOhlc(open, high, low, close, i)
    if (!c || !isNearSwingHigh(high, i)) return null
    if (!isBearish(c) || !isLongBody(c)) return null
    if (!hasTinyUpperShadow(c)) return null
    if (c.open < c.high * 0.998) return null
    return [i]
}

const detectThreeBlackCrowsAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 2) return null
    const candles = [i - 2, i - 1, i].map((idx) => toOhlc(open, high, low, close, idx))
    if (candles.some((c) => !c)) return null
    const [c1, c2, c3] = candles as OhlcCandle[]
    if (!isBearish(c1) || !isBearish(c2) || !isBearish(c3)) return null
    if (!isLongBody(c1) || !isLongBody(c2) || !isLongBody(c3)) return null
    if (c2.open > c1.open || c3.open > c2.open) return null
    if (c2.close >= c1.close || c3.close >= c2.close) return null
    return [i - 2, i - 1, i]
}

const detectThreeLineStrikeBearishAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 3) return null
    const c1 = toOhlc(open, high, low, close, i - 3)
    const c2 = toOhlc(open, high, low, close, i - 2)
    const c3 = toOhlc(open, high, low, close, i - 1)
    const c4 = toOhlc(open, high, low, close, i)
    if (!c1 || !c2 || !c3 || !c4) return null
    if (!isBearish(c1) || !isBearish(c2) || !isBearish(c3) || !isBullish(c4)) return null
    if (!isLongBody(c1) || !isLongBody(c2) || !isLongBody(c3) || !isLongBody(c4)) return null
    if (c4.open < c3.close || c4.close < c1.open) return null
    return [i - 3, i - 2, i - 1, i]
}

const detectHangingManAt: DetectorFn = ({ open, high, low, close }, i) => {
    const c = toOhlc(open, high, low, close, i)
    if (!c || !isHammerShaped(c) || !isNearSwingHigh(high, i)) return null
    return [i]
}

const detectUpsideGapTwoCrowsAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 2) return null
    const c1 = toOhlc(open, high, low, close, i - 2)
    const c2 = toOhlc(open, high, low, close, i - 1)
    const c3 = toOhlc(open, high, low, close, i)
    if (!c1 || !c2 || !c3) return null
    if (!isBullish(c1) || !isBearish(c2) || !isBearish(c3)) return null
    if (!isLongBody(c1)) return null
    if (!hasGapUp(c1, c2)) return null
    if (c3.close >= c2.close) return null
    return [i - 2, i - 1, i]
}

const detectEveningStarAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 2) return null
    const c1 = toOhlc(open, high, low, close, i - 2)
    const c2 = toOhlc(open, high, low, close, i - 1)
    const c3 = toOhlc(open, high, low, close, i)
    if (!c1 || !c2 || !c3) return null
    if (!isBullish(c1) || !isBearish(c3)) return null
    if (!isLongBody(c1) || !isLongBody(c3)) return null
    if (!isSmallBody(c2)) return null
    if (bodyBottom(c2) < bodyTop(c1) || bodyTop(c3) < bodyBottom(c2)) return null
    return [i - 2, i - 1, i]
}

const detectShootingStarAt: DetectorFn = ({ open, high, low, close }, i) => {
    const c = toOhlc(open, high, low, close, i)
    if (!c || !isShootingStarShaped(c) || !isNearSwingHigh(high, i)) return null
    return [i]
}

const detectBearishHaramiAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 1) return null
    const prev = toOhlc(open, high, low, close, i - 1)
    const curr = toOhlc(open, high, low, close, i)
    if (!prev || !curr) return null
    if (!isBullish(prev) || !isBearish(curr)) return null
    if (!isLongBody(prev) || !isSmallBody(curr)) return null
    if (bodyTop(curr) > bodyTop(prev) || bodyBottom(curr) < bodyBottom(prev)) return null
    return [i - 1, i]
}

const detectBearishDojiStarAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 1) return null
    const prev = toOhlc(open, high, low, close, i - 1)
    const curr = toOhlc(open, high, low, close, i)
    if (!prev || !curr) return null
    if (!isBullish(prev) || !isDoji(curr)) return null
    if (!isLongBody(prev)) return null
    return [i - 1, i]
}

const detectBearishAbandonedBabyAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 2) return null
    const c1 = toOhlc(open, high, low, close, i - 2)
    const c2 = toOhlc(open, high, low, close, i - 1)
    const c3 = toOhlc(open, high, low, close, i)
    if (!c1 || !c2 || !c3) return null
    if (!isBullish(c1) || !isDoji(c2) || !isBearish(c3)) return null
    if (!isLongBody(c1) || !isLongBody(c3)) return null
    if (!hasGapUp(c1, c2) || !hasGapDown(c2, c3)) return null
    return [i - 2, i - 1, i]
}

const detectBearishKickerAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 1) return null
    const prev = toOhlc(open, high, low, close, i - 1)
    const curr = toOhlc(open, high, low, close, i)
    if (!prev || !curr) return null
    if (!isBullish(prev) || !isBearish(curr)) return null
    if (!isLongBody(prev) || !isLongBody(curr)) return null
    if (curr.open >= prev.open) return null
    return [i - 1, i]
}

const detectThreeInsideDownAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 2) return null
    const c1 = toOhlc(open, high, low, close, i - 2)
    const c2 = toOhlc(open, high, low, close, i - 1)
    const c3 = toOhlc(open, high, low, close, i)
    if (!c1 || !c2 || !c3) return null
    if (!isBullish(c1) || !isBearish(c2) || !isBearish(c3)) return null
    const midpoint = (c1.open + c1.close) / 2
    if (bodyTop(c2) > bodyTop(c1) || bodyBottom(c2) < bodyBottom(c1)) return null
    if (c2.close >= midpoint || c3.close >= c1.open) return null
    return [i - 2, i - 1, i]
}

const detectThreeOutsideDownAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 2) return null
    const c1 = toOhlc(open, high, low, close, i - 2)
    const c2 = toOhlc(open, high, low, close, i - 1)
    const c3 = toOhlc(open, high, low, close, i)
    if (!c1 || !c2 || !c3) return null
    if (!isBullish(c1) || !isBearish(c2) || !isBearish(c3)) return null
    if (c2.open < c1.close || c2.close > c1.open) return null
    if (candleBody(c2) <= candleBody(c1)) return null
    if (c3.close >= c2.close) return null
    return [i - 2, i - 1, i]
}

const detectBearishMatHoldAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 4) return null
    const indices = [i - 4, i - 3, i - 2, i - 1, i]
    const candles = indices.map((idx) => toOhlc(open, high, low, close, idx))
    if (candles.some((c) => !c)) return null
    const [c1, c2, c3, c4, c5] = candles as OhlcCandle[]
    if (!isBearish(c1) || !isLongBody(c1)) return null
    if (!isBullish(c2) || !isBullish(c3) || !isBullish(c4)) return null
    if (!isBearish(c5) || !isLongBody(c5)) return null
    if ([c2, c3, c4].some((c) => c.low < c1.low || c.high > c1.high)) return null
    if (c5.close >= c1.low) return null
    return indices
}

const detectDarkCloudCoverAt: DetectorFn = ({ open, high, low, close }, i) => {
    if (i < 1) return null
    const prev = toOhlc(open, high, low, close, i - 1)
    const curr = toOhlc(open, high, low, close, i)
    if (!prev || !curr) return null
    if (!isBullish(prev) || !isBearish(curr)) return null
    if (!isLongBody(prev)) return null
    const midpoint = (prev.open + prev.close) / 2
    if (curr.open <= prev.high || curr.close >= midpoint || curr.close <= prev.open) return null
    return [i - 1, i]
}

export type CandlestickPatternDef = {
    kind: CandlestickPatternKind
    detectMethod: string
    detectAt: DetectorFn
    bias: ChartPatternBias
    label: string
    markerLetter: string
}

function candlestickDef(
    kind: CandlestickPatternKind,
    detectMethod: string,
    detectAt: DetectorFn,
    bias: ChartPatternBias,
): CandlestickPatternDef {
    return {
        kind,
        detectMethod,
        detectAt,
        bias,
        label: getTradingViewCandlestickPatternName(kind),
        markerLetter: getTradingViewCandlestickMarkerLetter(kind),
    }
}

export const CANDLESTICK_PATTERN_DEFS: CandlestickPatternDef[] = [
    candlestickDef('BULLISH_ENGULFING', 'detectBullishEngulfingPattern', detectBullishEngulfingAt, 'bullish'),
    candlestickDef('HAMMER', 'detectHammerPattern', detectHammerAt, 'bullish'),
    candlestickDef('MORNING_STAR', 'detectMorningStarPattern', detectMorningStarAt, 'bullish'),
    candlestickDef('PIERCING_LINE', 'detectPiercingLinePattern', detectPiercingLineAt, 'bullish'),
    candlestickDef('BULLISH_HARAMI', 'detectBullishHaramiPattern', detectBullishHaramiAt, 'bullish'),
    candlestickDef('THREE_WHITE_SOLDIERS', 'detectThreeWhiteSoldiersPattern', detectThreeWhiteSoldiersAt, 'bullish'),
    candlestickDef('INVERTED_HAMMER', 'detectInvertedHammerPattern', detectInvertedHammerAt, 'bullish'),
    candlestickDef('DRAGONFLY_DOJI', 'detectDragonflyDojiPattern', detectDragonflyDojiAt, 'bullish'),
    candlestickDef('BULLISH_ABANDONED_BABY', 'detectBullishAbandonedBabyPattern', detectBullishAbandonedBabyAt, 'bullish'),
    candlestickDef('THREE_INSIDE_UP', 'detectThreeInsideUpPattern', detectThreeInsideUpAt, 'bullish'),
    candlestickDef('THREE_OUTSIDE_UP', 'detectThreeOutsideUpPattern', detectThreeOutsideUpAt, 'bullish'),
    candlestickDef('BULLISH_KICKER', 'detectBullishKickerPattern', detectBullishKickerAt, 'bullish'),
    candlestickDef('TWEEZER_BOTTOM', 'detectTweezerBottomPattern', detectTweezerBottomAt, 'bullish'),
    candlestickDef('RISING_THREE_METHODS', 'detectRisingThreeMethodsPattern', detectRisingThreeMethodsAt, 'bullish'),
    candlestickDef('CONCEALING_BABY_SWALLOW', 'detectConcealingBabySwallowPattern', detectConcealingBabySwallowAt, 'bullish'),
    candlestickDef('MAT_HOLD', 'detectMatHoldPattern', detectMatHoldAt, 'bullish'),
    candlestickDef('BULLISH_SEPARATING_LINES', 'detectBullishSeparatingLinesPattern', detectBullishSeparatingLinesAt, 'bullish'),
    candlestickDef('BULLISH_BELT_HOLD', 'detectBullishBeltHoldPattern', detectBullishBeltHoldAt, 'bullish'),
    candlestickDef('THREE_LINE_STRIKE_BULLISH', 'detectThreeLineStrikeBullishPattern', detectThreeLineStrikeBullishAt, 'bullish'),
    candlestickDef('LADDER_BOTTOM', 'detectLadderBottomPattern', detectLadderBottomAt, 'bullish'),
    candlestickDef('MEETING_LINES', 'detectMeetingLinesPattern', detectMeetingLinesAt, 'bullish'),
    candlestickDef('BEARISH_ENGULFING', 'detectBearishEngulfingPattern', detectBearishEngulfingAt, 'bearish'),
    candlestickDef('BEARISH_BELT_HOLD', 'detectBearishBeltHoldPattern', detectBearishBeltHoldAt, 'bearish'),
    candlestickDef('THREE_BLACK_CROWS', 'detectThreeBlackCrowsPattern', detectThreeBlackCrowsAt, 'bearish'),
    candlestickDef('THREE_LINE_STRIKE_BEARISH', 'detectThreeLineStrikeBearishPattern', detectThreeLineStrikeBearishAt, 'bearish'),
    candlestickDef('HANGING_MAN', 'detectHangingManPattern', detectHangingManAt, 'bearish'),
    candlestickDef('UPSIDE_GAP_TWO_CROWS', 'detectUpsideGapTwoCrowsPattern', detectUpsideGapTwoCrowsAt, 'bearish'),
    candlestickDef('EVENING_STAR', 'detectEveningStarPattern', detectEveningStarAt, 'bearish'),
    candlestickDef('SHOOTING_STAR', 'detectShootingStarPattern', detectShootingStarAt, 'bearish'),
    candlestickDef('BEARISH_HARAMI', 'detectBearishHaramiPattern', detectBearishHaramiAt, 'bearish'),
    candlestickDef('BEARISH_DOJI_STAR', 'detectBearishDojiStarPattern', detectBearishDojiStarAt, 'bearish'),
    candlestickDef('BEARISH_ABANDONED_BABY', 'detectBearishAbandonedBabyPattern', detectBearishAbandonedBabyAt, 'bearish'),
    candlestickDef('TWEEZER_TOP', 'detectTweezerTopPattern', detectTweezerTopAt, 'bearish'),
    candlestickDef('BEARISH_KICKER', 'detectBearishKickerPattern', detectBearishKickerAt, 'bearish'),
    candlestickDef('THREE_INSIDE_DOWN', 'detectThreeInsideDownPattern', detectThreeInsideDownAt, 'bearish'),
    candlestickDef('THREE_OUTSIDE_DOWN', 'detectThreeOutsideDownPattern', detectThreeOutsideDownAt, 'bearish'),
    candlestickDef('BEARISH_MAT_HOLD', 'detectBearishMatHoldPattern', detectBearishMatHoldAt, 'bearish'),
    candlestickDef('DARK_CLOUD_COVER', 'detectDarkCloudCoverPattern', detectDarkCloudCoverAt, 'bearish'),
]

function runCandlestickDetect(
    open: number[],
    high: number[],
    low: number[],
    close: number[],
    timestamps: number[],
    def: CandlestickPatternDef,
    options: ChartPatternDetectOptions = {},
): ChartPattern[] {
    const detect = resolvePatternDetector(def, options)
    const sliced = sliceRecentOpenHighLowClose(open, high, low, close, timestamps, PATTERN_DETECTION_LOOKBACK_BARS)
    return detectAt(
        sliced,
        detect,
        def.kind,
        def.bias,
        def.label,
        def.markerLetter,
        options,
    )
}

export const CANDLESTICK_DETECT_EXPORTS: Record<string, CandlestickPatternDef> = Object.fromEntries(
    CANDLESTICK_PATTERN_DEFS.map((def) => [def.detectMethod, def]),
)

export function detectBullishEngulfingPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[0], options) }

export function detectHammerPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[1], options) }

export function detectMorningStarPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[2], options) }

export function detectPiercingLinePattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[3], options) }

export function detectBullishHaramiPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[4], options) }

export function detectThreeWhiteSoldiersPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[5], options) }

export function detectInvertedHammerPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[6], options) }

export function detectDragonflyDojiPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[7], options) }

export function detectBullishAbandonedBabyPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[8], options) }

export function detectThreeInsideUpPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[9], options) }

export function detectThreeOutsideUpPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[10], options) }

export function detectBullishKickerPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[11], options) }

export function detectTweezerBottomPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[12], options) }

export function detectRisingThreeMethodsPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[13], options) }

export function detectConcealingBabySwallowPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[14], options) }

export function detectMatHoldPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[15], options) }

export function detectBullishSeparatingLinesPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[16], options) }

export function detectBullishBeltHoldPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[17], options) }

export function detectThreeLineStrikeBullishPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[18], options) }

export function detectLadderBottomPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[19], options) }

export function detectMeetingLinesPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[20], options) }

export function detectBearishEngulfingPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[21], options) }

export function detectBearishBeltHoldPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[22], options) }

export function detectThreeBlackCrowsPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[23], options) }

export function detectThreeLineStrikeBearishPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[24], options) }

export function detectHangingManPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[25], options) }

export function detectUpsideGapTwoCrowsPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[26], options) }

export function detectEveningStarPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[27], options) }

export function detectShootingStarPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[28], options) }

export function detectBearishHaramiPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[29], options) }

export function detectBearishDojiStarPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[30], options) }

export function detectBearishAbandonedBabyPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[31], options) }

export function detectTweezerTopPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[32], options) }

export function detectBearishKickerPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[33], options) }

export function detectThreeInsideDownPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[34], options) }

export function detectThreeOutsideDownPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[35], options) }

export function detectBearishMatHoldPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[36], options) }

export function detectDarkCloudCoverPattern(
    open: number[], high: number[], low: number[], close: number[], timestamps: number[], options?: ChartPatternDetectOptions,
) { return runCandlestickDetect(open, high, low, close, timestamps, CANDLESTICK_PATTERN_DEFS[37], options) }
